import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { CheckCircle2, Phone, Mail, Clock, Home as HomeIcon } from 'lucide-react';

const ApplicationMain = () => {
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerid');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  const copyToClipboard = () => {
    if (customerId) {
      navigator.clipboard.writeText(customerId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <SEO 
        title="Application Submitted Successfully | Legal Services"
        description="Thank you for your interest in our legal services. We have received your application and will contact you shortly."
      />
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Success Icon and Message */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Application Submitted Successfully!
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Thank you for choosing our legal services. We have received your application and our team will review it shortly.
            </p>
          </div>

          {/* Customer ID Card */}
          {customerId && (
            <div className="bg-white rounded-lg shadow-lg p-8 mb-8 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Your Reference ID</h2>
                <button
                  onClick={copyToClipboard}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  {copied ? '✓ Copied!' : 'Copy ID'}
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Customer ID</p>
                <p className="text-2xl font-mono font-bold text-gray-900 break-all">
                  {customerId}
                </p>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Please save this reference ID for future correspondence. You can use it to track your application status.
              </p>
            </div>
          )}

          {/* Next Steps */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">What Happens Next?</h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Application Review</h3>
                  <p className="text-gray-600">
                    Our legal experts will carefully review your application and requirements within 24-48 hours.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Personal Consultation</h3>
                  <p className="text-gray-600">
                    One of our legal advisors will contact you to discuss your needs and answer any questions you may have.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Service Initiation</h3>
                  <p className="text-gray-600">
                    Once all details are confirmed, we'll begin processing your case with utmost care and professionalism.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-8 mb-8 text-white">
            <h2 className="text-2xl font-bold mb-6">Need Immediate Assistance?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <Phone className="w-6 h-6 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold mb-1">Call Us</p>
                  <p className="text-blue-100">+1 (888) 472 1093</p>
                  <p className="text-sm text-blue-200 mt-1">Mon-Fri: 9 AM - 6 PM EST</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail className="w-6 h-6 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold mb-1">Email Us</p>
                  <p className="text-blue-100">support@legalservices.com</p>
                  <p className="text-sm text-blue-200 mt-1">We respond within 24 hours</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              <HomeIcon className="w-5 h-5" />
              Return to Home
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-colors"
            >
              Learn More About Us
            </Link>
          </div>

          {/* Additional Information */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 text-gray-600 bg-white px-6 py-3 rounded-full shadow-md">
              <Clock className="w-5 h-5" />
              <span className="text-sm font-medium">Average response time: 24 hours</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApplicationMain;
